import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { submitContact } from '../api/contact';

const TYPE_OPTIONS = [
  { value: 'request', label: '機能の要望' },
  { value: 'bug',     label: 'バグ・不具合の報告' },
  { value: 'inquiry', label: 'その他のお問い合わせ' },
] as const;

type ContactType = typeof TYPE_OPTIONS[number]['value'];

export default function ContactPage() {
  const { user } = useAuth();
  const [type, setType] = useState<ContactType>('request');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;

    setIsSubmitting(true);
    setError('');
    try {
      await submitContact({
        type,
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim() || undefined,
      });
      setSubmitted(true);
    } catch {
      setError('送信に失敗しました。しばらく待ってから再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">送信しました</h1>
        <p className="text-gray-500 text-sm">
          お問い合わせありがとうございます。<br />
          内容を確認のうえ、必要に応じてご連絡いたします。
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-8 px-4">
      <h1 className="text-xl font-bold text-gray-900 mb-1">要望・お問い合わせ</h1>
      <p className="text-sm text-gray-500 mb-6">
        機能の要望やバグ報告、ご質問などをお気軽にどうぞ。
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* タイプ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            種別 <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setType(opt.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  type === opt.value
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* タイトル */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            タイトル <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="一言で内容を説明してください"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            required
          />
          <div className="text-right text-xs text-gray-400 mt-0.5">{subject.length}/200</div>
        </div>

        {/* 内容 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            内容 <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
            rows={6}
            placeholder={
              type === 'bug'
                ? '再現手順・発生環境（ブラウザ、OS）・期待する動作などを詳しく教えてください'
                : type === 'request'
                ? 'どんな機能があると嬉しいか、具体的な使い方などを教えてください'
                : 'ご質問・ご意見をお書きください'
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={2000}
            required
          />
          <div className="text-right text-xs text-gray-400 mt-0.5">{message.length}/2000</div>
        </div>

        {/* メールアドレス（未ログイン時は任意入力） */}
        {!user && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              メールアドレス <span className="text-xs text-gray-400">（返信が必要な場合）</span>
            </label>
            <input
              type="email"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={254}
            />
          </div>
        )}

        {/* ログイン状態の表示 */}
        {user && (
          <p className="text-xs text-gray-400">
            @{user.x_username} としてログイン中のアカウントに紐付けて送信されます
          </p>
        )}

        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !subject.trim() || !message.trim()}
          className="w-full py-2.5 bg-green-600 text-white font-medium rounded-lg disabled:opacity-50 hover:bg-green-700 transition-colors"
        >
          {isSubmitting ? '送信中...' : '送信する'}
        </button>
      </form>
    </div>
  );
}
